import { withZod } from "@remix-validated-form/with-zod";
import { z } from "zod";
import { zfd } from "zod-form-data";

export const contactFormFields = {
  recaptcha: z.string({
    required_error: "Veuillez cocher la case 'Je ne suis pas un robot'.",
    invalid_type_error: "Veuillez cocher la case 'Je ne suis pas un robot'.",
  }),
  favorite_brand: z.any(),
  firstName: z
    .string()
    .min(1, "Veuillez renseigner un prénom.")
    .max(64, "Le prénom ne doit pas dépasser 64 caractères.")
    .regex(/^[^\d]*$/, "Le prénom ne doit pas contenir de chiffres."),
  lastName: z
    .string()
    .min(1, "Veuillez renseigner un nom.")
    .max(64, "Le nom ne doit pas dépasser 64 caractères.")
    .regex(/^[^\d]*$/, "Le nom ne doit pas contenir de chiffres."),
  email: z
    .string()
    .min(1, "Veuillez renseigner un email.")
    .regex(
      /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/,
      "L'email n'est pas au bon format."
    ),
  phone: z
    .string()
    .min(1, "Veuillez renseigner un numéro de téléphone.")
    .max(64, "Le numéro de téléphone ne doit pas dépasser 64 caractères."),
  country: z
    .string()
    .min(1, "Veuillez renseigner le pays.")
    .max(64, "Le pays ne doit pas dépasser 64 caractères."),
  zipcode: z.string().min(1, "Veuillez renseigner un code postal."),
  city: z
    .string()
    .min(1, "Veuillez renseigner la ville.")
    .max(64, "La ville ne doit pas dépasser 64 caractères."),
  message: z.string().min(1, "Veuillez renseigner un message."),
  files: z
    .any()
    .refine((files: FileList | File) => {
      if (!files) return true;
      if ("length" in files) {
        const array = Array.from(files);
        let isValid = true;
        array.forEach((file) => {
          isValid = isValid && file.size < 10_000_000;
        });
        return isValid;
      } else {
        return files.size < 10_000_000;
      }
    }, "Les fichiers ne doivent pas dépasser 10 Mo chacun.")
    .refine((files: FileList | File) => {
      if (!files) return true;
      if ("length" in files) {
        const array = Array.from(files);
        let isValid = true;
        array.forEach((file) => {
          isValid =
            isValid &&
            (file.type === "image/jpeg" ||
              file.type === "image/png" ||
              file.type === "application/pdf");
        });
        return isValid;
      } else {
        return (
          files.size === 0 ||
          files.type === "image/jpeg" ||
          files.type === "image/png" ||
          files.type === "application/pdf"
        );
      }
    }, "Les fichiers doivent être au format pdf, jpeg ou png.")
    .transform((files: FileList | File) => {
      if (!files) return null;
      if ("length" in files) {
        return Array.from(files);
      } else if (files.size === 0) {
        return null;
      } else {
        return [files];
      }
    })
    .optional()
    .nullable(),
  checkboxContact: zfd
    .checkbox()
    .refine((val) => val, "Vous devez accepter d'être recontacté par notre équipe."),
  dataProcessingAllowed: zfd.checkbox(),
};

const contactFormSchema = z.discriminatedUnion("contactType", [
  z.object({
    contactType: z.literal("pro"),
    subject: z.string().min(1, "Veuillez renseigner l'objet de votre demande."),
    ...contactFormFields,
  }),
  z.object({
    contactType: z.literal("customer"),
    ...contactFormFields,
  }),
]);

const selectContactFormSchema = z.object({
  contact: z
    .string({
      required_error: "Veuillez sélectionner un compte client.",
      invalid_type_error: "Veuillez sélectionner un compte client.",
    })
    .min(1, "Veuillez sélectionner un compte client."),
});

export const contactFormValidator = withZod(contactFormSchema);
export const selectContactFormValidator = withZod(selectContactFormSchema);
